// Components/Modals/InfoModal.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Slide,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "24px",
    boxShadow:
      "0 32px 64px rgba(5, 50, 81, 0.2), 0 16px 32px rgba(5, 50, 81, 0.1)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8fffe 100%)",
    border: "1px solid rgba(124, 200, 144, 0.1)",
    position: "relative",
    overflow: "visible",
    maxWidth: "520px",
    width: "100%",
    margin: "16px",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(5, 50, 81, 0.4)",
    backdropFilter: "blur(8px)",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background:
    "linear-gradient(135deg, rgba(5, 50, 81, 0.02) 0%, rgba(124, 200, 144, 0.02) 100%)",
  borderBottom: "1px solid rgba(5, 50, 81, 0.08)",
  padding: "32px 50px 24px 32px",
  position: "relative",
  direction: "rtl",
  textAlign: "right",
  "& span": {
    fontSize: "28px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: "1.3",
    letterSpacing: "-0.5px",
    display: "block",
    width: "100%",
  },
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  left: "16px",
  top: "16px",
  backgroundColor: "rgba(5, 50, 81, 0.05)",
  color: "#053251",
  width: "40px",
  height: "40px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#053251",
    color: "white",
    transform: "rotate(90deg)",
    boxShadow: "0 8px 16px rgba(5, 50, 81, 0.2)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "32px",
  paddingTop: "24px",
  paddingBottom: "24px",
  position: "relative",
  direction: "rtl",
}));

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
  marginRight: "24px",
  textAlign: "right",
  direction: "rtl",
  "& p": {
    fontSize: "16px",
    lineHeight: "1.75",
    color: "#053251",
    marginBottom: "16px",
    fontWeight: "400",
    letterSpacing: "0.1px",
    position: "relative",
    textAlign: "right",
    "&:last-child": {
      marginBottom: 0,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      right: "-20px",
      top: "12px",
      width: "6px",
      height: "6px",
      backgroundColor: "#7cc890",
      borderRadius: "50%",
      opacity: 0.6,
    },
    // ✅ Style for danger items
    "&.danger": {
      fontWeight: "700",
      color: "#dc3545",
      "&::before": {
        backgroundColor: "#dc3545",
      },
    },
    // ✅ Style for bold items
    "&.bold": {
      fontWeight: "700",
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "24px 32px 32px",
  background:
    "linear-gradient(135deg, rgba(124, 200, 144, 0.03) 0%, rgba(5, 50, 81, 0.01) 100%)",
  borderTop: "1px solid rgba(5, 50, 81, 0.06)",
  gap: "16px",
  justifyContent: "flex-start",
  direction: "rtl",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "32px",
    right: "32px",
    height: "1px",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(124, 200, 144, 0.3) 50%, transparent 100%)",
  },
}));

const StyledButton = styled(Button)(
  ({ theme, variant: buttonVariant, color }) => ({
    borderRadius: "14px",
    padding: "14px 32px",
    fontSize: "15px",
    fontWeight: "600",
    textTransform: "none",
    letterSpacing: "0.5px",
    minWidth: "120px",
    position: "relative",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "none",
    ...(color === "success" && {
      background: "linear-gradient(135deg, #7cc890 0%, #6bb57f 100%)",
      color: "white",
      border: "1px solid rgba(124, 200, 144, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #6bb57f 0%, #5aa46e 100%)",
        boxShadow: "0 12px 24px rgba(124, 200, 144, 0.3)",
        transform: "translateY(-2px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
    }),
    ...(color === "primary" && {
      background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
      color: "white",
      border: "1px solid rgba(5, 50, 81, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #2a5a72 0%, #1e4159 100%)",
        boxShadow: "0 12px 24px rgba(5, 50, 81, 0.3)",
        transform: "translateY(-2px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
    }),
    "&.Mui-disabled": {
      background: "rgba(5, 50, 81, 0.12)",
      color: "rgba(5, 50, 81, 0.38)",
      border: "1px solid transparent",
      boxShadow: "none",
      transform: "none",
    },
  })
);

const AcknowledgementBox = styled("label")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  margin: "0 0 4px",
  padding: "10px 12px",
  borderRadius: "14px",
  border: "1px solid rgba(5, 50, 81, 0.14)",
  background: "rgba(124, 200, 144, 0.1)",
  cursor: "pointer",
  direction: "rtl",
  textAlign: "right",
  "& span": {
    fontSize: "15px",
    fontWeight: 600,
    color: "#053251",
    lineHeight: 1.6,
  },
});

const InfoModal = ({
  handleCloseDialog,
  openDialog,
  text, // ✅ Can be array of strings OR array of objects {text, style}
  confirnButtonText,
  title,
  handleAction,
  actionButtonText,
  requireAcknowledgement = false,
  acknowledgementText = "توضیحات بالا را خواندم",
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const canDismiss = !requireAcknowledgement || acknowledged;

  useEffect(() => {
    if (openDialog) {
      setAcknowledged(false);
    }
  }, [openDialog]);

  const requestClose = () => {
    if (!canDismiss) return;
    handleCloseDialog();
  };
  // ✅ Helper function to render text items
  const renderTextItem = (item, index) => {
    // Handle both string and object formats
    if (typeof item === "string") {
      return (
        <p className="iran-sans" key={index}>
          {item}
        </p>
      );
    }

    // Object format: {text: "...", style: "normal|bold|danger"}
    const className = `iran-sans ${item.style || "normal"}`;
    return (
      <p className={className} key={index}>
        {item.text}
      </p>
    );
  };

  return (
    <StyledDialog
      open={openDialog}
      onClose={requestClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth={false}
      style={{ zIndex: "10000" }}
    >
      <StyledDialogTitle>
        <span>{title}</span>
        <StyledCloseButton
          onClick={requestClose}
          disabled={!canDismiss}
          aria-label="بستن"
          sx={!canDismiss ? { opacity: 0.35 } : undefined}
        >
          <CloseIcon />
        </StyledCloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <StyledDialogContentText className="mt-3">
          {text &&
            Array.isArray(text) &&
            text.map((item, index) => renderTextItem(item, index))}
        </StyledDialogContentText>
      </StyledDialogContent>

      <StyledDialogActions sx={{ flexWrap: "wrap" }}>
        {requireAcknowledgement && (
          <AcknowledgementBox className="iran-sans">
            <Checkbox
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              sx={{
                padding: "4px",
                color: "#053251",
                "&.Mui-checked": { color: "#5aa46e" },
              }}
            />
            <span>{acknowledgementText}</span>
          </AcknowledgementBox>
        )}
        <StyledButton
          variant="contained"
          onClick={requestClose}
          color="success"
          className="ms-2"
          disabled={!canDismiss}
        >
          <span>{confirnButtonText}</span>
        </StyledButton>
        {actionButtonText && (
          <StyledButton
            variant="contained"
            onClick={handleAction}
            color="primary"
            className="me-2"
          >
            <span>{actionButtonText}</span>
          </StyledButton>
        )}
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default InfoModal;
